/**
 * `globalThis.Buffer`, for the wallet chunk and nothing else.
 *
 * ## Why this file exists
 *
 * `@coinbase/wallet-sdk` references the bare Node global `Buffer` — in
 * `dist/vendor-js/eth-eip712-util/` and in `WalletLinkSigner.js`, both of which
 * are on the *signing* path. Vite does not polyfill Node globals, so in a
 * browser the SDK throws `ReferenceError: Buffer is not defined` the moment a
 * user signs. The SDK catches it and re-raises it as an internal RPC error, so
 * what the user sees is
 *
 *     An internal error was received. Details: Buffer is not defined
 *
 * attributed to viem, which is neither the cause nor anywhere near it. Sign-in
 * is the first thing every user does, and it failed for everyone who picked
 * Coinbase Wallet.
 *
 * ## Why no test caught it
 *
 * **A surface nothing reaches is a surface nothing checks.** `e2e/wallet-
 * fixture.ts` injects a signer, so all 85 browser tests exercise the signing
 * *logic* — the SIWE message, the EIP-712 structs, the golden digests — and
 * never a real connector. The one code path this file repairs is reachable only
 * by clicking a button in a browser with the Coinbase SDK actually loaded, and
 * nothing in this repository does that. The suite was green the whole time.
 *
 * That makes it worth stating plainly: the connector list is a wire surface
 * like any other, and `connectors.ts` names three entries of which the tests
 * exercise one — the mock. Adding a connector adds an untested path.
 *
 * ## Why here, and why dynamic
 *
 * `buffer` is ~7 KB gzip and belongs to nobody but the wallet stack. The
 * `import()` below keeps it out of the entry chunk, which is what invariant 19
 * measures: `perf-budget.json` is checked against `dist/index.html` — the entry
 * module, its `modulepreload`s, the stylesheet and the preloaded fonts. A
 * *static* import here would put the polyfill on the critical path for every
 * anonymous visitor, which is the same mistake `wallet/lazy.ts`'s whole module
 * comment exists to prevent, one dependency smaller.
 *
 * It is a **direct** dependency rather than the copy already in the tree.
 * `buffer@6.0.3` arrives transitively today through `cbw-sdk` and
 * `@reown/appkit-polyfills`; both are wagmi's own transitive deps, either could
 * drop it in a patch release, and the failure that produces is this same
 * message returning with no change in our source to explain it.
 */

/**
 * Install `globalThis.Buffer` if the runtime has none.
 *
 * Idempotent, and it **never overwrites an existing `Buffer`**. A real Node
 * global (SSR, a test runner, an embedded webview that provides one) is a
 * better implementation than the shim and may already be holding data; the
 * check is `typeof` rather than truthiness so a runtime that defines it as
 * something falsy is still left alone.
 *
 * Deliberately not a module-level side effect. Importing a module must not
 * mutate a global, or the order two importers happen to load in becomes part
 * of the behaviour — and `loadWalletConfig` needs this to have *finished*
 * before the SDK's module body runs, which an import side effect cannot
 * promise across a dynamic boundary.
 */
export async function installBufferShim(): Promise<void> {
  if (typeof (globalThis as { Buffer?: unknown }).Buffer !== 'undefined') return

  const { Buffer } = await import('buffer')
  ;(globalThis as { Buffer?: unknown }).Buffer = Buffer
}
